/** A command the composer offers when the message starts with `/` (an agent's `<slash_commands>`). */
export interface ComposerCommand {
  /** Without the slash: "health-check". */
  name: string;
  description?: string;
}

/**
 * What's typed after a leading `/`, while it can still name a command; null otherwise. The list is for the
 * command itself, so it closes at the first space (what follows is the message) or a line break.
 */
export function commandQuery(text: string): string | null {
  if (!text.startsWith("/")) return null;
  const query = text.slice(1);
  return /\s/.test(query) ? null : query;
}

/** Commands whose name starts with the query, in their declared order (case doesn't matter). */
export function matchingCommands(commands: readonly ComposerCommand[], query: string): ComposerCommand[] {
  const q = query.toLowerCase();
  return commands.filter((command) => command.name.toLowerCase().startsWith(q));
}

/** The message once a command is picked: the command and a space, ready for anything more. */
export const withCommand = (command: ComposerCommand) => `/${command.name} `;

export type CommandKeyResult =
  | { type: "move"; index: number }
  | { type: "pick"; command: ComposerCommand }
  | { type: "close" }
  | null;

/**
 * A key pressed in the message box while the command list is open. Arrows move the highlight (stopping at the ends),
 * Enter or Tab picks the highlighted command, Escape closes the list. Null: the key isn't the list's.
 */
export function commandKey(
  key: string,
  matches: readonly ComposerCommand[],
  highlighted: number,
  modifiers: { shiftKey?: boolean } = {},
): CommandKeyResult {
  switch (key) {
    case "ArrowDown":
      return matches.length ? { type: "move", index: Math.min(highlighted + 1, matches.length - 1) } : null;
    case "ArrowUp":
      return matches.length ? { type: "move", index: Math.max(highlighted - 1, 0) } : null;
    case "Enter":
    case "Tab": {
      if (modifiers.shiftKey) return null;
      const command = matches[highlighted];
      return command ? { type: "pick", command } : null;
    }
    case "Escape":
      return { type: "close" };
    default:
      return null;
  }
}
